import React, { forwardRef, useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import AkChevronRightIcon from '@atlaskit/icon/core/migration/chevron-right';
import { useOverflowStatus } from '@atlaskit/atlassian-navigation';

import { ButtonItem } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';
import { useTopMenus } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import { testIdGenerate } from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { Link } from '@atlassian/react-resource-router';
import { useMenuHighlighted } from '../../../../controllers/highlight/main.tsx';
import { useNavigationItemAnalytics } from '../../../../controllers/navigation-item-analytics/main.tsx';
import { PrimaryButton } from '../../primary-button/index.tsx';
import { PrimaryDropdownButton } from '../../primary-dropdown-button/index.tsx';
import { LinkItem } from '../common/ui/link-item/index.tsx';
import { RouteLinkItem } from '../common/ui/route-link-item/index.tsx';
import type { MenuTriggerProps } from './types.tsx';

export const MenuTrigger = forwardRef<HTMLElement, MenuTriggerProps>(
	(
		{
			href,
			menuId,
			navigationItemId,
			onBeforeMenuOpen = () => undefined,
			params,
			query,
			replace,
			to,
			type,
			analyticsAttributes,
			...rest
		},
		ref,
	) => {
		const [isMenuOpen, { off: closeMenu, toggle: toggleMenu }] = useTopMenus(menuId);
		const { isVisible } = useOverflowStatus();
		const isHighlighted = useMenuHighlighted(menuId);
		const triggerAnalytics = useNavigationItemAnalytics({ navigationItemId });

		const onClick = useCallback(() => {
			if (!isMenuOpen) {
				onBeforeMenuOpen();
			}
			toggleMenu();
			triggerAnalytics(analyticsAttributes);
		}, [analyticsAttributes, isMenuOpen, onBeforeMenuOpen, toggleMenu, triggerAnalytics]);

		// close menu on unmount
		useEffect(() => closeMenu, [closeMenu]);

		const testId = testIdGenerate('primary-actions', menuId, 'menu-trigger');

		if (isVisible) {
			return href != null ? (
				<PrimaryButton
					{...rest}
					component={Link}
					href={href}
					isHighlighted={isHighlighted}
					onClick={onClick}
					ref={ref}
					testId={testId}
					// @ts-expect-error - Type '"a"' is not assignable to type '"submit" | "reset" | "button" | undefined'
					type="a"
				/>
			) : (
				// @ts-expect-error - iconBefore types mismatch ReactNode vs ReactChild
				<PrimaryDropdownButton
					{...rest}
					isHighlighted={isHighlighted}
					isSelected={isMenuOpen}
					onClick={onClick}
					ref={ref}
					testId={testId}
				/>
			);
		}

		if (to != null && __SPA__) {
			return (
				<RouteLinkItem
					{...rest}
					onClick={onClick}
					params={params}
					query={query}
					replace={replace}
					testId={testId}
					to={to}
					type={type}
				/>
			);
		}

		return href != null ? (
			<LinkItem {...rest} href={href} onClick={onClick} testId={testId} />
		) : (
			<span ref={ref}>
				<ButtonItem
					{...rest}
					iconAfter={<ChevronRightIcon />}
					isSelected={isMenuOpen}
					onClick={onClick}
					testId={testId}
				/>
			</span>
		);
	},
);

// re-render regression detection
// @ts-expect-error - TS2339 - Property 'whyDidYouRender' does not exist on type 'ForwardRefExoticComponent<Pick<any, string | number | symbol> & RefAttributes<MenuTriggerProps>>'.
MenuTrigger.whyDidYouRender = {
	customName: 'MenuTrigger',
};

export const ChevronRightIcon = () => (
	<ChevronWrapper>
		<AkChevronRightIcon spacing="spacious" label="" size="small" />
	</ChevronWrapper>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChevronWrapper = styled.span({
	opacity: 0.5,
	marginRight: token('space.negative.200'),
});
